import { computed, watch } from 'vue'
import { useAuth, useUser } from '@clerk/vue'
import { useConvexClient, useConvexMutation } from 'convex-vue'
import { useRouter } from 'vue-router'
import { api } from '@/convex'
import { useProfileStore } from '@/stores/profile'
import { useOnboardingNavigation } from '@/features/onboarding/composables/useOnboardingNavigation'
import { getClerkUserDisplayFields } from '@/lib/clerkUserFields'
import {
  GUEST_ONLY_HOME_PATH,
  guestBookingDetailAppointmentId,
  isBookingIdentityRequiredRoute,
  isBookingPublicRoute,
  isBookingRoute,
  isBookingSignedInRequiredRoute,
  isGuestOnlyAllowedRoute,
  bookingHandleFromPath,
  isPublicRoute,
  isSessionOptionalRoute,
} from '@/lib/authRoutes'
import type { LocationQuery, LocationQueryRaw } from 'vue-router'

function bookingRedirectQuery(query: LocationQuery): LocationQueryRaw {
  const next: LocationQueryRaw = {}
  for (const [key, value] of Object.entries(query)) {
    if (key === 'profileId' || value === undefined || value === null) {
      continue
    }
    if (Array.isArray(value)) {
      const strings = value.filter((entry): entry is string => typeof entry === 'string')
      if (strings.length > 0) {
        next[key] = strings
      }
      continue
    }
    if (typeof value === 'string') {
      next[key] = value
    }
  }
  return next
}

function guestBookingCatalogPath(path: string): string {
  const handle = bookingHandleFromPath(path)
  return handle ? `/${handle}` : '/'
}
import { isCompleteGuestSession, readBookingGuestPending } from '@/features/booking/lib/bookingGuestPending'
import { resolveBookingGuestIdentity } from '@/features/booking/lib/resolveBookingGuestIdentity'
import { isGuestOnlyAccount, readClerkAccountRole } from '@/lib/accountAccess'

function waitUntilLoaded(isLoaded: { value: boolean }): Promise<void> {
  if (isLoaded.value) return Promise.resolve()
  return new Promise((resolve) => {
    const stop = watch(
      () => isLoaded.value,
      (loaded) => {
        if (loaded) {
          stop()
          resolve()
        }
      },
      { immediate: true },
    )
  })
}

let sessionConvexAuthWired = false

/**
 * Clerk + Convex auth + onboarding in one place:
 * - Router guard awaits Clerk load, wires Convex JWT, ensures profile, then resolves onboarding.
 * - Booking routes skip provider onboarding; guests continue the booking flow.
 * - Booking-only accounts (guest row / guest Clerk role, no `profiles` row) keep their session but stay
 *   outside the provider app — they are sent to their guest bookings page.
 * - A small watch keeps Convex token fetchers in sync and re-runs the guard after sign-in when the URL does not change.
 * - `canRenderRoute` is the shell gate: protected pages unmount the instant the session dies.
 */
export function useAuthRouteGuard() {
  const { isLoaded, isSignedIn, getToken, sessionClaims } = useAuth()
  const { user, isLoaded: isUserLoaded } = useUser()
  const convex = useConvexClient()
  const router = useRouter()
  const profileStore = useProfileStore()
  const { replaceWithNextStep } = useOnboardingNavigation()
  const { mutate: ensureProfile } = useConvexMutation(api.users.ensureProfile)

  /** Shell may mount `router-view` only when Clerk is ready and the path is allowed for current session. */
  const canRenderRoute = computed(() => {
    if (!isLoaded.value) return false
    const path = router.currentRoute.value.path
    if (isSignedIn.value) {
      // Booking-only accounts must not flash the provider shell while the guard redirects them.
      if (!profileStore.isGuestOnlyAccount) return true
      return isBookingRoute(path) || isPublicRoute(path) || isGuestOnlyAllowedRoute(path)
    }
    if (isBookingSignedInRequiredRoute(path)) return false
    if (isSessionOptionalRoute(path)) return true
    return isBookingRoute(path) && isCompleteGuestSession(readBookingGuestPending())
  })

  async function fetchConvexToken(forceRefreshToken: boolean): Promise<string | null> {
    try {
      const useConvexIntegration = sessionClaims.value?.aud === 'convex'
      return (
        (await getToken.value({
          ...(useConvexIntegration ? {} : { template: 'convex' }),
          skipCache: forceRefreshToken,
        })) ?? null
      )
    } catch {
      return null
    }
  }

  function syncConvexAuthFromClerk() {
    convex.setAuth(({ forceRefreshToken }) => fetchConvexToken(forceRefreshToken))
  }

  watch(
    [isLoaded, isSignedIn],
    ([loaded, signedIn], [prevLoaded, prevSignedIn]) => {
      if (!loaded) return
      if (!signedIn) {
        convex.client.clearAuth()
        return
      }
      syncConvexAuthFromClerk()
      if (prevLoaded === true && prevSignedIn === false && signedIn === true) {
        profileStore.resetProfileState()
        // Booking calendar keeps slot selection in component state — remount
        // wipes it and disables Tovább while queries reload.
        if (!isBookingRoute(router.currentRoute.value.path)) {
          void router.replace(router.currentRoute.value.fullPath)
        }
      }
    },
    { immediate: true },
  )

  router.beforeResolve(async (to) => {
    await waitUntilLoaded(isLoaded)
    if (!isSignedIn.value) {
      convex.client.clearAuth()
      profileStore.resetProfileState()
      sessionConvexAuthWired = false
      if (isBookingSignedInRequiredRoute(to.path)) {
        return { path: guestBookingCatalogPath(to.path), query: bookingRedirectQuery(to.query), replace: true }
      }
      if (isPublicRoute(to.path) || isBookingPublicRoute(to.path)) return true
      const guestAppointmentId = guestBookingDetailAppointmentId(to.path)
      if (guestAppointmentId) {
        // Email links: sign in through the provider's identify step, then come back here.
        const context = await convex
          .query(api.bookings.guestActions.getSignInContext, { appointmentId: guestAppointmentId })
          .catch(() => null)
        if (context) {
          return {
            path: `/${context.profileId}/service/${context.serviceId}/identify`,
            query: { returnTo: to.fullPath },
            replace: true,
          }
        }
        // Provider gone: regular login, then back to the link.
        return { path: '/login', query: { redirect_url: to.fullPath }, replace: true }
      }
      if (isBookingRoute(to.path)) {
        if (isCompleteGuestSession(readBookingGuestPending())) return true
        // Calendar/summary without session → identify (service id from path when present).
        const serviceMatch = to.path.match(/^\/([^/]+)\/service\/([^/]+)/)
        if (serviceMatch?.[1] && serviceMatch[2] && bookingHandleFromPath(to.path)) {
          return {
            path: `/${serviceMatch[1]}/service/${serviceMatch[2]}/identify`,
            query: bookingRedirectQuery(to.query),
            replace: true,
          }
        }
        return { path: guestBookingCatalogPath(to.path), query: bookingRedirectQuery(to.query), replace: true }
      }
      return { path: '/login', replace: true }
    }

    if (!sessionConvexAuthWired) {
      syncConvexAuthFromClerk()
      sessionConvexAuthWired = true
    }

    // Guests in booking flow: wire auth only — no provider profile / onboarding.
    if (isBookingRoute(to.path)) {
      if (!isBookingIdentityRequiredRoute(to.path)) {
        return true
      }
      if (isCompleteGuestSession(readBookingGuestPending())) {
        return true
      }
      await waitUntilLoaded(isUserLoaded)
      try {
        const guestProfile = await convex.query(api.guests.endpoints.currentGuestProfile, {})
        if (
          resolveBookingGuestIdentity({
            guest: guestProfile,
            clerk: getClerkUserDisplayFields(user.value),
            pending: readBookingGuestPending(),
          })
        ) {
          return true
        }
      } catch {
        // JWT not ready or Convex unreachable — fall through to identify.
      }
      const serviceMatch = to.path.match(/^\/([^/]+)\/service\/([^/]+)/)
      if (serviceMatch?.[1] && serviceMatch[2] && bookingHandleFromPath(to.path)) {
        return {
          path: `/${serviceMatch[1]}/service/${serviceMatch[2]}/identify`,
          query: bookingRedirectQuery(to.query),
          replace: true,
        }
      }
      return { path: guestBookingCatalogPath(to.path), query: bookingRedirectQuery(to.query), replace: true }
    }

    await waitUntilLoaded(isUserLoaded)

    if (!profileStore.hasCheckedProfile) {
      try {
        await fetchConvexToken(false)
        const access = await convex.query(api.users.currentAccountAccess, {})
        if (isGuestOnlyAccount(access, readClerkAccountRole(user.value?.unsafeMetadata))) {
          // Never call ensureProfile here — it would turn a booking account into a provider.
          profileStore.markGuestOnlyAccount()
        } else {
          const profile = await ensureProfile(getClerkUserDisplayFields(user.value))
          profileStore.setProfile(profile ? { onboardingCompleted: profile.onboardingCompleted } : null)
        }
      } catch {
        // Let navigation proceed if Convex is unreachable so the shell can still render.
        profileStore.markProfileCheckedWithoutData()
      }
    }

    if (profileStore.isGuestOnlyAccount) {
      // Provider pages stay closed, but the session survives so the guest can read their bookings.
      if (isGuestOnlyAllowedRoute(to.path)) {
        return true
      }
      return { path: GUEST_ONLY_HOME_PATH, replace: true }
    }

    if (profileStore.profile?.onboardingCompleted === false) {
      let state
      try {
        state = await convex.query(api.onboarding.endpoints.onboardingState, {})
      } catch {
        return true
      }
      const redirect = replaceWithNextStep(state?.answers)
      if (!redirect) {
        if (to.meta.isOnboarding === true) {
          return { path: '/', replace: true }
        }
        return true
      }
      if (to.path !== redirect.path) {
        return redirect
      }
    }

    return true
  })

  return { canRenderRoute }
}
